/**
 * stripeBillingService.ts — Wrapper for the two new Stripe-billing endpoints.
 *
 * - createCheckoutSession() → POST /subscription/create-stripe-session
 *      Returns { url } that the caller should window.location.assign() to.
 * - openCustomerPortal()     → POST /subscription/customer-portal
 *      Returns { url } for self-service cancel/payment-update.
 *
 * Both endpoints require an Authorization: Bearer <jwt> header.
 */

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

export type Billing = 'monthly' | 'yearly';

export interface CheckoutSessionResponse {
  url: string;
  sessionId: string;
}

export interface PortalSessionResponse {
  url: string;
}

/**
 * BillingRequestError carries the machine-readable purchase-rules
 * refusal `code` alongside the human-facing `message`, so the UI can
 * pick an appropriate friendly copy without exposing the raw code to
 * customers. See api/auth/src/purchaseRules.js for the canonical list.
 */
export class BillingRequestError extends Error {
  status: number;
  code?: string;
  activeBundle?: string;
  supersededBy?: string;
  constructor(message: string, status: number, extras?: { code?: string; activeBundle?: string; supersededBy?: string }) {
    super(message);
    this.name = 'BillingRequestError';
    this.status = status;
    this.code = extras?.code;
    this.activeBundle = extras?.activeBundle;
    this.supersededBy = extras?.supersededBy;
  }
}

async function postJson<T>(path: string, token: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body ?? {}),
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let code: string | undefined;
    let activeBundle: string | undefined;
    let supersededBy: string | undefined;
    try {
      const data = await res.json();
      if (data && typeof data.error === 'string') message = data.error;
      if (data && typeof data.code === 'string') code = data.code;
      if (data && typeof data.activeBundle === 'string') activeBundle = data.activeBundle;
      if (data && typeof data.supersededBy === 'string') supersededBy = data.supersededBy;
    } catch {
      /* fall through with generic message */
    }
    throw new BillingRequestError(message, res.status, { code, activeBundle, supersededBy });
  }

  return res.json() as Promise<T>;
}

/**
 * Create a Stripe Checkout Session for the given product + billing.
 * Caller should redirect to the returned `url`.
 */
export async function createCheckoutSession(
  token: string,
  product: string,
  billing: Billing = 'monthly',
): Promise<CheckoutSessionResponse> {
  return postJson<CheckoutSessionResponse>(
    '/subscription/create-stripe-session',
    token,
    { product, billing },
  );
}

/**
 * Get a one-time-use Stripe Customer Portal URL for self-service mgmt.
 * Caller should redirect to the returned `url`.
 *
 * Throws if the user has no `stripeCustomerId` (e.g., grandfathered PayPal user).
 */
export async function openCustomerPortal(token: string): Promise<PortalSessionResponse> {
  return postJson<PortalSessionResponse>('/subscription/customer-portal', token, {});
}
